import React from 'react';
import { Select } from 'antd';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { emptyFieldDash } from 'utils/empty-field-helper';

import s from './styles.module.scss';

import type { IBaseSelectBoxProps } from './types';

const BaseSelectBox: React.FC<IBaseSelectBoxProps> = (props) => {
  const { t } = useTranslation();

  const {
    value,
    placeholder = t('controls.select'),
    disabled,
    plaintext,
    readonly,
    invalid,
    onChange,
    className,
    style,
    allowClear = false,
    allowSearch = false,
    mode,
    maxWidth,
    minWidth,
    defaultValue,
    prefix,
    icon,
    iconClassName = s['suffix-icon'],
    labelRender,
    options = [],
    dataCy = 'base-select-box',
    size,
    onDeselect,
    onClear,
    maxTagTextLength,
    maxTagCount,
    loading,
    filterOption,
    popupClassName,
    useUndefinedValue = false,
    open,
    onDropdownVisibleChange,
  } = props;

  const selectBoxClassNames = classNames(
    s.select,
    {
      [s.readonly]: readonly,
    },
    className,
  );

  const dropdownClassNames = classNames(s.dropdown, popupClassName);
  const selectValue = defaultValue ? undefined : value ?? null;

  if (plaintext && (selectValue === null || selectValue === undefined)) {
    return <span data-cy={dataCy}>{emptyFieldDash(undefined)}</span>;
  }

  return (
    <div data-cy={dataCy}>
      <Select
        loading={loading}
        style={{
          width: '100%',
          minWidth,
          maxWidth,
          ...style,
        }}
        popupClassName={dropdownClassNames}
        options={options}
        mode={mode}
        value={useUndefinedValue ? undefined : selectValue}
        onChange={(val: any, option: any) => {
          if (onChange) onChange(val ?? null, option);
        }}
        disabled={disabled}
        placeholder={placeholder}
        className={selectBoxClassNames}
        variant={plaintext ? 'borderless' : 'outlined'}
        status={invalid ? 'error' : undefined}
        prefix={prefix}
        suffixIcon={
          disabled || readonly
            ? null
            : icon && <span className={iconClassName}>{icon}</span>
        }
        allowClear={allowClear}
        labelRender={labelRender}
        defaultValue={defaultValue}
        optionRender={(option) => (
          <div data-cy="select-option">{option.label}</div>
        )}
        size={size}
        onClear={onClear}
        onDeselect={onDeselect}
        optionFilterProp="label"
        maxTagTextLength={maxTagTextLength}
        maxTagCount={maxTagCount}
        showSearch={allowSearch}
        filterOption={filterOption}
        open={open}
        onDropdownVisibleChange={onDropdownVisibleChange}
      />
    </div>
  );
};

export const BaseSelectBoxDisplayName = 'BaseSelectBox';
BaseSelectBox.displayName = BaseSelectBoxDisplayName;

export default BaseSelectBox;
